import type { Certificate } from 'model/User/types';

export interface UserToNotify {
  id: string;
  name: string;
  email: string;
}

export interface CertificateCategory {
  id: string;
  name: string;
  deletable: boolean;
}

export interface OverviewCertificate {
  description: string;
  /** UTC Z */
  expiryDate: string | null;
  expireWarning: number;
  id: string;
  mandatory: boolean;
  /** When true, a user can hold multiple current certificates of this type (catch-all course
   * types). When false (default), a new upload supersedes the previous one. */
  allowMultiple: boolean;
  numberOfUsers: number;
  typeId: string;
  categoryId: string | null;
  categoryName: string | null;
  usersToNotify: string[];
  usersToNotifyList: UserToNotify[];
}

export interface CompanyCertificateSettings {
  companyId: string;
  usersToNotify: string[];
  usersToNotifyList: UserToNotify[];
}

export interface CertificatesMetadata {
  totalCount: number;
  countsByCategory: Record<string, number>;
  uncategorizedCount: number;
}

export interface CertificateType {
  id: string;
  companyId?: string;
  recVersion?: number;
  companyIds?: string[];
  typeId: string;
  description: string;
  mandatory: boolean;
  allowMultiple: boolean;
  issuer?: string;
  issuerWebSite?: string;
  priority?: number;
  expireWarning: number;
  categoryId: string | null;
  categoryName: string | null;
  usersToNotify: string[];
  usersToNotifyList: UserToNotify[];
}

export interface UserCertificate {
  companyId: string;
  createdFromCompanyId: string;
  id: string;
  user: {
    companyProfileId: string;
    id: string;
    name: string;
  };
  typeGuid: string;
  typeId: string;
  /** UTC Z */
  issued: string | null;
  notes: string;
  /** UTC Z */
  validTo: string | null;
  numberOfDocuments: number;
  /** UTC Z */
  expiryNotificationDate: string | null;
  isEditable: boolean;
  /** Whether the cert's type allows multiple current certificates. Drives the Renew action
   * (renew is offered only when false). */
  allowMultiple: boolean;
  lastUsedProjectName: string | null;
  categoryId: string | null;
  categoryName: string | null;
}

export type BatchUpdateUserCertificate = Pick<
  UserCertificate,
  'id' | 'typeId' | 'issued' | 'notes' | 'validTo' | 'expiryNotificationDate'
> & {
  userId: string;
};

export enum SortingField {
  Name,
  Type,
  Issued,
  ValidTo,
  Documents,
  Expiry,
  Notes,
}

/**
 * Discriminates the certificates-overview row shape from the per-user profile shape.
 * `numberOfDocuments` exists only on `UserCertificate`.
 *
 * Lives here, next to the interfaces it discriminates, rather than in the component that first
 * needed it — `services/` and other non-component modules consume it, and importing a component
 * file from a service pulls a whole React subtree into its module graph and forms an import cycle.
 */
export const isUserCertificate = (
  certificate: UserCertificate | Certificate
): certificate is UserCertificate => 'numberOfDocuments' in certificate;
